import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Tooltip, Typography,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { apiConstants, get } from '../../common/constants'
import Loader from '../../common/Loader'
import PageTitle from '../../components/PageTitle'
import { formatDate } from '../../common/util'
import CopyIcon from '../../common/CopyIcon'

const fmtKg = (v, dp = 3) =>
  v == null || isNaN(v) ? '—' : `${Number(v).toFixed(dp)} kg`
const fmtKg2 = (v) => (v == null || isNaN(v) ? '—' : `${Number(v).toFixed(2)} kg`)

// calcStatus → user-facing chip per the API doc's mapping table.
// Uses .includes() so future BE phrasing tweaks don't break the match.
function statusChipFor(calcStatus) {
  const s = String(calcStatus || '').toUpperCase()
  if (!s) return null
  if (s.includes('EXACT')) return { label: 'Exact Match', color: '#15803d', bg: '#dcfce7' }
  if (s.includes('ALL INDIVIDUAL')) return { label: 'Full Match', color: '#15803d', bg: '#dcfce7' }
  if (s.includes('PARTIAL')) return { label: 'Partial Match', color: '#a16207', bg: '#fef3c7' }
  if (s.includes('SINGLE UNIT') || s.includes('SINGLE_AVG')) return { label: 'Avg Based', color: '#1e40af', bg: '#dbeafe' }
  if (s.includes('NO SHIPMENT') || s.includes('NO_HISTORY')) return { label: 'No History', color: '#b91c1c', bg: '#fee2e2' }
  if (s.includes('NO-SKU') || s.includes('NO_SKU')) return null // doc says N/A
  return { label: calcStatus, color: '#6b7280', bg: '#f3f4f6' }
}

// ── Calc metadata strip — status + freeze/DWS overrides ────────────────────
// Surfaces the prediction method and any manual overrides applied. Shown
// above the equation card so the user knows what kind of calc they're
// looking at before reading the numbers.
function CalcMetadataStrip({ calcStatus, exactMatchUsed, weightFreezeApplied, customDwsApplied, weightFreezeRef }) {
  const chip = statusChipFor(calcStatus)
  const hasAnyChip = chip || weightFreezeApplied || customDwsApplied
  if (!hasAnyChip) return null
  return (
    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' rowGap={1} sx={{ mb: 1.5 }}>
      {chip && (
        <Chip
          size='small'
          label={chip.label}
          sx={{ height: 24, fontSize: 11.5, fontWeight: 600, bgcolor: chip.bg, color: chip.color }}
        />
      )}
      {exactMatchUsed && (
        <Chip
          size='small'
          variant='outlined'
          label='Based on exact history'
          sx={{ height: 24, fontSize: 11, color: '#16a34a', borderColor: '#86efac' }}
        />
      )}
      {weightFreezeApplied && (
        <Tooltip title={weightFreezeRef ? `Reference: ${weightFreezeRef}` : 'Predicted weight was manually frozen for this shipment'}>
          <Chip
            size='small'
            label='Weight Freeze Applied'
            sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: '#e0e7ff', color: '#4338ca' }}
          />
        </Tooltip>
      )}
      {customDwsApplied && (
        <Tooltip title='A custom Dimensional Weight Surcharge rule was applied to this shipment'>
          <Chip
            size='small'
            label='Custom DWS Applied'
            sx={{ height: 24, fontSize: 11, fontWeight: 600, bgcolor: '#fce7f3', color: '#be185d' }}
          />
        </Tooltip>
      )}
    </Stack>
  )
}

// ── Equation strip — Σ avg − packaging = pushed ─────────────────────────────
// When exactMatchUsed is true, the BE chose the combo-history average over
// the per-SKU equation result. We keep the equation honest by showing the
// equation's actual result (= individualFallbackWeight) in the equals box,
// then place a SEPARATE card next to it for the exact-match override which
// is what we actually pushed to the courier.
function EquationCard({
  rawCalc, packagingPct, packagingAmt,
  finalWeight, individualFallbackWeight, exactMatchUsed,
}) {
  const pctDisplay =
    packagingPct != null ? `${(Number(packagingPct) * 100).toFixed(0)}%` : '—'
  // What the equation actually produces. Falls back to finalWeight when the
  // BE didn't supply individualFallbackWeight (typical when no exact match).
  const equationResult = individualFallbackWeight ?? finalWeight
  const showOverride = exactMatchUsed && individualFallbackWeight != null
  // When override is shown, shrink the result column from md=4 to md=2 and
  // give the new override card md=2 so they sit side-by-side. Otherwise the
  // single result card keeps the original md=4 width.
  const resultColMd = showOverride ? 2 : 4

  return (
    <Card sx={{ mb: 2, bgcolor: '#f5f3ff' }}>
      <CardContent sx={{ py: 2.5 }}>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12} md={3} textAlign='center'>
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Sum of SKU Avg Weights</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#111' }}>
              {fmtKg(rawCalc)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={1} textAlign='center'>
            <Typography sx={{ fontSize: 28, color: '#9ca3af', fontWeight: 300 }}>−</Typography>
          </Grid>
          <Grid item xs={12} md={3} textAlign='center'>
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
              Packaging Adjustment ({pctDisplay})
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>
              {packagingAmt != null ? `−${fmtKg(packagingAmt)}` : '—'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.25 }}>
              Single vs multiple packing material
            </Typography>
          </Grid>
          <Grid item xs={12} md={1} textAlign='center'>
            <Typography sx={{ fontSize: 28, color: '#9ca3af', fontWeight: 300 }}>=</Typography>
          </Grid>

          {/* Equation result — the actual math output (= individualFallbackWeight) */}
          <Grid item xs={12} md={resultColMd}>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 1.5,
                p: 1.5,
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(99,102,241,0.15)',
                border: '1px solid #e0e7ff',
              }}
            >
              <Typography sx={{ fontSize: 11.5, color: '#6366f1', fontWeight: 600 }}>
                {showOverride ? 'Per-SKU Fallback' : 'Predicted Weight'}
              </Typography>
              {/* Use 3-decimal precision so 3.875 doesn't get rounded to 3.88 */}
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#4338ca', mt: 0.25 }}>
                {fmtKg(equationResult)}
              </Typography>
              {showOverride && (
                <Typography sx={{ fontSize: 10.5, color: '#9ca3af', mt: 0.25 }}>
                  Sum − packaging adj.
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Exact-match override card — only when exact match won */}
          {showOverride && (
            <Grid item xs={12} md={2}>
              <Box
                sx={{
                  bgcolor: '#dcfce7',
                  borderRadius: 1.5,
                  p: 1.5,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(22,163,74,0.18)',
                  border: '1px solid #86efac',
                  position: 'relative',
                }}
              >
                <Typography sx={{ fontSize: 11.5, color: '#15803d', fontWeight: 700 }}>
                  Predicted Weight
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#065f46', mt: 0.25 }}>
                  {fmtKg2(finalWeight)}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: '#16a34a', mt: 0.25 }}>
                  Exact combo match used
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  )
}

// ── Banner shown when an exact match drove the prediction ───────────────────
// Banner gates on the historical-shipment array, not just calcStatus, so we
// never show "X shipments were found" with an empty count + no button.
// (Older records may carry calcStatus EXACT but an empty comboHistory; in
// that case there's nothing to show — hide the banner.)
function ExactMatchBanner({ status, comboCount, isMultiSku, onViewMatches }) {
  if (!comboCount) return null
  const s = String(status || '').toUpperCase()
  if (!s.includes('EXACT')) return null
  const message = isMultiSku
    ? `${comboCount} historical shipments with the exact same SKU combination were found. These take preference over individual SKU averages.`
    : `Exact-quantity historical match found across ${comboCount} shipment${comboCount === 1 ? '' : 's'}. Average was used as the prediction.`
  return (
    <Alert
      icon={<LayersOutlinedIcon sx={{ color: '#16a34a' }} />}
      severity='success'
      sx={{
        mb: 2,
        bgcolor: '#ecfdf5',
        color: '#065f46',
        border: '1px solid #d1fae5',
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>
            Exact Match Found!
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#047857', mt: 0.25 }}>{message}</Typography>
        </Box>
        <Button
          variant='contained'
          color='success'
          size='small'
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={onViewMatches}
          sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
        >
          View Exact Matches
        </Button>
      </Stack>
    </Alert>
  )
}

// ── Current Shipment SKU Combination — pill row of all SKUs in this AWB ────
function CurrentSkuCombination({ products = [] }) {
  if (!products.length) return null
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.25 }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 18, color: '#6366f1' }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            Current Shipment SKU Combination
          </Typography>
        </Stack>
        <Stack direction='row' spacing={1.25} flexWrap='wrap' rowGap={1.25}>
          {products.map((p, i) => (
            <Box
              key={`${p.sku}-${i}`}
              sx={{
                bgcolor: '#eef2ff',
                border: '1px solid #c7d2fe',
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                minWidth: 140,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#4338ca' }}>
                {p.sku || '—'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                {p.skuName || ''}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#6366f1', fontWeight: 600, mt: 0.25 }}>
                Qty: {p.quantity ?? 0}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

// ── Per-SKU breakdown ───────────────────────────────────────────────────────
function PerSkuSection({ sku, snapshot, isOnlySku }) {
  const last3AwbSet = useMemo(
    () => new Set((sku.last3Shipments || []).map((s) => s.awb)),
    [sku],
  )
  const allShipments = sku.allShipments?.length ? sku.allShipments : sku.last3Shipments || []

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 18, color: '#6366f1' }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            <Box component='span' sx={{ color: '#3b82f6' }}>{sku.sku || '—'}</Box>
            {' — '}
            {sku.skuName || ''}
            <Box component='span' sx={{ color: '#6b7280', fontWeight: 500, ml: 1 }}>
              (Qty: {sku.quantity ?? 0} in this shipment)
            </Box>
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          {/* All historical shipments — stays full width until lg so the
              6-col table doesn't get squashed when the sidebar is expanded. */}
          <Grid item xs={12} lg={7}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
              All Historical Shipments ({allShipments.length})
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size='small' sx={{ '& td, & th': { fontSize: 11.5, py: 0.5, whiteSpace: 'nowrap' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { color: '#6b7280', fontWeight: 700, bgcolor: '#f5f7fa' } }}>
                    <TableCell sx={{ width: 36 }}>#</TableCell>
                    <TableCell>AWB</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Courier</TableCell>
                    <TableCell align='right'>Transit Charged Wt</TableCell>
                    <TableCell align='right'>Charged Slab</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allShipments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                        No historical shipments for this SKU.
                      </TableCell>
                    </TableRow>
                  )}
                  {allShipments.map((s, i) => {
                    const highlighted = last3AwbSet.has(s.awb)
                    return (
                      <TableRow
                        key={`${s.awb}-${i}`}
                        sx={{ bgcolor: highlighted ? '#ede9fe' : 'transparent' }}
                      >
                        <TableCell sx={{ color: '#9ca3af' }}>{i + 1}</TableCell>
                        <TableCell sx={{ color: '#3b82f6', fontWeight: 600 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {s.awb || '—'}
                            <CopyIcon text={s.awb} />
                          </Box>
                        </TableCell>
                        <TableCell>{s.shipmentDate ? formatDate(s.shipmentDate, { format: 'yyyy-MM-dd' }) : '—'}</TableCell>
                        <TableCell>{s.courierPartnerName || '—'}</TableCell>
                        <TableCell align='right'>{fmtKg(s.courierActualWeight)}</TableCell>
                        <TableCell align='right'>
                          <Chip
                            size='small'
                            label={fmtKg(s.courierActualWeightSlab)}
                            sx={{ height: 20, fontSize: 10.5, bgcolor: '#fef3c7', color: '#92400e' }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
            <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 1 }}>
              <Box component='span' sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#ede9fe', borderRadius: 0.5, mr: 0.75, verticalAlign: 'middle' }} />
              Highlighted rows = Last 3 shipments used for average
            </Typography>
          </Grid>

          {/* Calculation panel */}
          <Grid item xs={12} lg={5}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
              <CalculateOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Weight Calculation
            </Typography>
            <Box sx={{ p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>
                Last 3 Shipments Weight
              </Typography>
              {(sku.last3Shipments || []).map((s, i) => (
                <Stack key={`${s.awb}-${i}`} direction='row' justifyContent='space-between' sx={{ fontSize: 12, py: 0.25 }}>
                  <Typography sx={{ fontSize: 12, color: '#374151' }}>
                    Shipment {i + 1}{' '}
                    <Box component='span' sx={{ color: '#9ca3af' }}>
                      ({s.shipmentDate ? formatDate(s.shipmentDate, { format: 'yyyy-MM-dd' }) : '—'})
                    </Box>
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{fmtKg(s.courierActualWeight)}</Typography>
                </Stack>
              ))}
              <Divider sx={{ my: 0.75 }} />
              <Stack direction='row' justifyContent='space-between'>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Average Weight per Unit</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6366f1' }}>
                  {fmtKg(sku.avgLast3CourierWeight)}
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 1.5, bgcolor: '#f5f3ff', borderRadius: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#6366f1', fontWeight: 700, mb: 0.5 }}>
                Subtotal for this SKU in Current Shipment
              </Typography>
              <Stack direction='row' justifyContent='space-between'>
                <Typography sx={{ fontSize: 12, color: '#374151' }}>
                  {fmtKg(sku.avgLast3CourierWeight)} × {sku.quantity ?? 0} units
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#4338ca' }}>
                  {fmtKg(sku.subtotalBeforeAdj)}
                </Typography>
              </Stack>
            </Box>

            {/* Dead/Vol weight only on single-SKU shipments — these are AWB-level fields */}
            {/* {isOnlySku && (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.25, border: '1px solid #e5e7eb', borderRadius: 1, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Dead Weight</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                      {snapshot?.deadWeightGms != null ? `${(snapshot.deadWeightGms / 1000).toFixed(2)} kg` : '—'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.25, border: '1px solid #e5e7eb', borderRadius: 1, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Volumetric Weight</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                      {snapshot?.volWeightGms != null ? `${(snapshot.volWeightGms / 1000).toFixed(2)} kg` : '—'}
                    </Typography>
                    {snapshot?.lengthCm && snapshot?.breadthCm && snapshot?.heightCm && (
                      <Typography sx={{ fontSize: 10.5, color: '#9ca3af', mt: 0.25 }}>
                        {snapshot.lengthCm} x {snapshot.breadthCm} x {snapshot.heightCm} cm
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )} */}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

// ── Bottom: per-SKU subtotal table + summary block ─────────────────────────
function FinalWeightTable({
  skuCalcDetails = [],
  rawCalc,
  packagingPct,
  packagingAmt,
  finalWeight,
  individualFallbackWeight,
  exactMatchUsed,
}) {
  const pctDisplay = packagingPct != null ? `${(Number(packagingPct) * 100).toFixed(0)}%` : '—'
  // Show the per-SKU fallback row (math result) only when it actually
  // differs from finalWeight — i.e. the BE used an exact-match override.
  const showFallbackRow =
    exactMatchUsed &&
    individualFallbackWeight != null &&
    Number(individualFallbackWeight) !== Number(finalWeight)
  return (
    <Card>
      <CardContent>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5 }}>
          <PercentOutlinedIcon sx={{ fontSize: 18, color: '#dc2626' }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            Final Weight with Packaging Adjustment
          </Typography>
        </Stack>
        <Table size='small' sx={{ '& td, & th': { fontSize: 12, py: 0.75 } }}>
          <TableHead>
            <TableRow sx={{ '& th': { color: '#6b7280', fontWeight: 700, bgcolor: '#f5f7fa' } }}>
              <TableCell>SKU</TableCell>
              <TableCell align='right'>Qty</TableCell>
              <TableCell align='right'>Avg Last 3 Wt/Unit</TableCell>
              <TableCell align='right'>Subtotal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {skuCalcDetails.map((sku, i) => (
              <TableRow key={`${sku.sku}-${i}`}>
                <TableCell>
                  <Box component='span' sx={{ color: '#3b82f6', fontWeight: 600 }}>{sku.sku}</Box>
                  {sku.skuName ? <Box component='span' sx={{ color: '#6b7280' }}> — {sku.skuName}</Box> : null}
                </TableCell>
                <TableCell align='right'>{sku.quantity ?? '—'}</TableCell>
                <TableCell align='right'>{fmtKg(sku.avgLast3CourierWeight)}</TableCell>
                <TableCell align='right' sx={{ fontWeight: 700 }}>{fmtKg(sku.subtotalBeforeAdj)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ mt: 1 }}>
          <Stack direction='row' justifyContent='space-between' sx={{ py: 1, borderTop: '1px solid #e5e7eb' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Raw Calculated Weight</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{fmtKg(rawCalc)}</Typography>
          </Stack>
          <Stack
            direction='row'
            justifyContent='space-between'
            sx={{ py: 1, px: 1, bgcolor: '#fef2f2', borderRadius: 0.75 }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
              Packaging Adjustment Factor
              <Box component='span' sx={{ color: '#dc2626', fontWeight: 500, ml: 0.5 }}>
                (−{pctDisplay} — single packing material vs individual)
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#b91c1c' }}>
              {packagingAmt != null ? `−${fmtKg(packagingAmt)}` : '—'}
            </Typography>
          </Stack>
          {/* Per-SKU Fallback row — only when exact-match override took over */}
          {showFallbackRow && (
            <Stack
              direction='row'
              justifyContent='space-between'
              sx={{ py: 1, px: 1, bgcolor: '#fafafa', borderRadius: 0.75, mt: 0.5, border: '1px dashed #d1d5db' }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6b7280' }}>
                Per-SKU Fallback Weight
                <Box component='span' sx={{ color: '#9ca3af', fontWeight: 500, ml: 0.5 }}>
                  (Raw − Packaging Adj — what would be used without exact match)
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#6b7280' }}>
                {fmtKg(individualFallbackWeight)}
              </Typography>
            </Stack>
          )}
          <Stack
            direction='row'
            justifyContent='space-between'
            sx={{ py: 1.25, px: 1, bgcolor: '#eef2ff', borderRadius: 0.75, mt: 0.5 }}
          >
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#4338ca' }}>
                Predicted Weight
              </Typography>
              {showFallbackRow && (
                <Typography sx={{ fontSize: 11, color: '#16a34a', mt: 0.25 }}>
                  Exact combo match used · saved {fmtKg(Number(individualFallbackWeight) - Number(finalWeight))}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#4338ca' }}>
              {fmtKg2(finalWeight)}
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function PredictedWeightPage() {
  const { awbNumber } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  // Prefer state passed from list to avoid an extra round-trip; fall back to GET if deep-linked.
  const [row, setRow] = useState(location.state?.row || null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (row && row.awbNumber === awbNumber) return
    setLoading(true)
    get(`${apiConstants.WEIGHT_LEDGER_BY_AWB}/${awbNumber}`)
      .then((res) => setRow(res?.data || null))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awbNumber])

  // Hooks MUST stay above any early return — react-hooks/rules-of-hooks.
  // Derive everything in one place and let downstream components handle the
  // null cases on their own.
  const calc = row?.prozoCalc
  const products = row?.products || []
  const totalUnits =
    row?.totalUnits ?? products.reduce((s, p) => s + (p.quantity || 0), 0)
  const isMultiSku = products.length > 1
  const skuCalcDetails = useMemo(() => calc?.skuCalcDetails || [], [calc])
  // For multi-SKU: BE returns prozoCalc.comboHistory.
  // For single-SKU: the exact-match shipments are skuCalcDetails[0].last3Shipments
  // when calcStatus signals an EXACT match (no separate combo array exists).
  const exactMatchShipments = useMemo(() => {
    if (!calc) return []
    if (isMultiSku) return calc.comboHistory || []
    const s = String(calc.calcStatus || '').toUpperCase()
    if (s.includes('EXACT')) return skuCalcDetails[0]?.last3Shipments || []
    return []
  }, [calc, isMultiSku, skuCalcDetails])
  const goToExactMatches = () =>
    navigate(`/weight/ledger/calc/${awbNumber}/exact-matches`, { state: { row } })

  if (loading) {
    return (
      <>
        <PageTitle>Prozo Weight Calculation</PageTitle>
        <Loader />
      </>
    )
  }

  if (!row) {
    return (
      <Box sx={{ p: 3 }}>
        <PageTitle>Prozo Weight Calculation</PageTitle>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back to Dashboard
        </Button>
        <Alert severity='warning' sx={{ mt: 2 }}>No record found for AWB {awbNumber}.</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <PageTitle>Prozo Weight Calculation</PageTitle>
      {/* Header strip */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.75 }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Box
                sx={{
                  width: 44, height: 44, borderRadius: 1.25,
                  bgcolor: '#6366f1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <CalculateOutlinedIcon sx={{ color: '#fff' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Prozo Weight Calculation</Typography>
                <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                  AWB: <Box component='span' sx={{ color: '#6366f1', fontWeight: 600 }}>{row.awbNumber}</Box>
                  <CopyIcon text={row.awbNumber} />
                  {' · '}
                  {totalUnits} unit{totalUnits === 1 ? '' : 's'}
                  {' · '}
                  {products.length} unique SKU{products.length === 1 ? '' : 's'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction='row' spacing={1}>
              <Chip size='small' label={row.courierPartnerName || row.courierPartnerProzoId || '—'} sx={{ height: 24, bgcolor: '#f3f4f6' }} />
              <Chip size='small' label={row.orderType?.split(' ')[0] || '—'} sx={{ height: 24, bgcolor: '#dbeafe', color: '#1e40af' }} />
              <Chip size='small' label={row.dispatchMode || '—'} sx={{ height: 24, bgcolor: '#dcfce7', color: '#15803d' }} />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/weight/ledger')}
        sx={{ mb: 1.5, color: '#6366f1', fontWeight: 600 }}
      >
        Back to Dashboard
      </Button>

      {!calc ? (
        <Alert severity='info'>
          Weight prediction not available — no SKU data for this shipment.
        </Alert>
      ) : (
        <>
          <CalcMetadataStrip
            calcStatus={calc.calcStatus}
            exactMatchUsed={calc.exactMatchUsed}
            weightFreezeApplied={row.weightFreezeApplied}
            customDwsApplied={row.customDwsApplied}
            weightFreezeRef={row.weightFreezeRef}
          />

          <EquationCard
            rawCalc={calc.rawCalcWeight}
            packagingPct={calc.packagingAdjPct}
            packagingAmt={calc.packagingAdjAmount}
            finalWeight={calc.finalWeight}
            individualFallbackWeight={calc.individualFallbackWeight}
            exactMatchUsed={calc.exactMatchUsed}
          />

          <ExactMatchBanner
            status={calc.calcStatus}
            comboCount={exactMatchShipments.length}
            isMultiSku={isMultiSku}
            onViewMatches={goToExactMatches}
          />

          <CurrentSkuCombination products={products} />

          {skuCalcDetails.map((sku, i) => (
            <PerSkuSection
              key={`${sku.sku}-${i}`}
              sku={sku}
              snapshot={row.clientDeclaredSnapshot}
              isOnlySku={skuCalcDetails.length === 1}
            />
          ))}

          <FinalWeightTable
            skuCalcDetails={skuCalcDetails}
            rawCalc={calc.rawCalcWeight}
            packagingPct={calc.packagingAdjPct}
            packagingAmt={calc.packagingAdjAmount}
            finalWeight={calc.finalWeight}
            individualFallbackWeight={calc.individualFallbackWeight}
            exactMatchUsed={calc.exactMatchUsed}
          />

        </>
      )}
    </Box>
  )
}
